import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { GoalStates } from '@interfaces/goalEnums';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import { useState } from 'react';
import { PortfolioSelectionType } from './portfolioSelectionType';
import { ReviewPortfolioChangesVisual } from './reviewPortfolioChanges.visual';

const UPDATE_SUB_ACCOUNT = gql`
  mutation updateSubAccount($input: UpdateSubAccountInput!) {
    updateSubAccount(input: $input) {
      subAccount {
        id
      }
    }
  }
`;

const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
      }
    }
  }
`;

export const FETCH_GOALS = gql`
  query fetchGoals($input: FetchGoalsInput!) {
    fetchGoals(input: $input) {
      goals {
        id
        name
        type
        financialProduct {
          id
          translatedUrl { ${TRANSLATED_STRING_FIELDS} }
          riskReturnLevel
          suggestable
          theme {
            translatedName { ${TRANSLATED_STRING_FIELDS} }
            translatedDescription { ${TRANSLATED_STRING_FIELDS} }
          }
        }
        suggestedFinancialProduct {
          id
          translatedUrl { ${TRANSLATED_STRING_FIELDS} }
          riskReturnLevel
          theme {
            translatedName { ${TRANSLATED_STRING_FIELDS} }
            translatedDescription { ${TRANSLATED_STRING_FIELDS} }
          }
        }
        subAccounts {
          id
          state
          account {
            type
          }
          financialProduct {
            id
            translatedUrl { ${TRANSLATED_STRING_FIELDS} }
            riskReturnLevel
            suggestable
            theme {
              translatedName { ${TRANSLATED_STRING_FIELDS} }
              translatedDescription { ${TRANSLATED_STRING_FIELDS} }
            }
          }
          suggestedFinancialProduct {
            id
            translatedUrl { ${TRANSLATED_STRING_FIELDS} }
            riskReturnLevel
            theme {
              translatedName { ${TRANSLATED_STRING_FIELDS} }
              translatedDescription { ${TRANSLATED_STRING_FIELDS} }
            }
          }
        }
      }
    }
  }
`;

export const ReviewPortfolioChanges = ({
  options,
  userId,
  onNext,
  stepLoading,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
}) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const { loading } = useQuery(FETCH_GOALS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          userId,
          states: GoalStates.ACTIVE,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    onCompleted: async (e) => {
      const updatedGoals = await processPortfolioToUpdate(e.fetchGoals.goals);
      setGoals([...updatedGoals]);
    },
  });

  const processPortfolioToUpdate = async (inputGoals: Goal[]) => {
    const updatedGoals = [...inputGoals];

    for (let i = 0; i < updatedGoals.length; i++) {
      const goal = updatedGoals[i];

      if (goal?.financialProduct) {
        if (updatedGoals[i]) {
          updatedGoals[i].portfolioSelectionType = goal
            ?.suggestedFinancialProduct?.id
            ? PortfolioSelectionType.SUGGESTED
            : PortfolioSelectionType.CURRENT;
        }
      }

      if (goal?.subAccounts && goal.subAccounts.length > 0) {
        for (let j = 0; j < goal.subAccounts.length; j++) {
          const subAccount = goal.subAccounts[j];

          if (subAccount?.financialProduct) {
            const currentSubAccount = updatedGoals[i]?.subAccounts?.[j];
            if (currentSubAccount) {
              currentSubAccount.portfolioSelectionType = subAccount
                ?.suggestedFinancialProduct?.id
                ? PortfolioSelectionType.SUGGESTED
                : PortfolioSelectionType.CURRENT;
            }
          }
        }
      }
    }

    return updatedGoals;
  };

  const [updateSubAccount, { loading: subAccountLoading }] =
    useMutation(UPDATE_SUB_ACCOUNT);
  const [updateGoal, { loading: goalLoading }] = useMutation(UPDATE_GOAL);

  const onContinue = async () => {
    if (goals && goals.length > 0) {
      for await (const goal of goals) {
        if (
          goal.financialProduct?.id !== goal.suggestedFinancialProduct?.id &&
          goal.portfolioSelectionType === PortfolioSelectionType.SUGGESTED
        ) {
          await updateGoal({
            variables: {
              input: {
                goalId: goal.id,
                financialProductId: goal.suggestedFinancialProduct?.id,
              },
            },
          });
        }

        if (
          !goal.financialProduct &&
          goal.subAccounts &&
          goal.subAccounts.length > 0
        ) {
          for await (const subAccount of goal.subAccounts) {
            if (
              subAccount.financialProduct?.id !==
                subAccount.suggestedFinancialProduct?.id &&
              subAccount.portfolioSelectionType ===
                PortfolioSelectionType.SUGGESTED
            ) {
              await updateSubAccount({
                variables: {
                  input: {
                    subAccountId: subAccount.id,
                    financialProductId:
                      subAccount.suggestedFinancialProduct?.id,
                  },
                },
              });
            }
          }
        }
      }
    }
    onNext();
  };

  return (
    <ReviewPortfolioChangesVisual
      options={options}
      continueFunc={onContinue}
      loading={loading || stepLoading || subAccountLoading || goalLoading}
      goals={goals}
      onUpdateGoals={setGoals}
    />
  );
};

export default ReviewPortfolioChanges;
